import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SeriesBadge } from "@/components/SeriesBadge";
import heroAsset from "@/assets/solair-awning-morning-coffee-porch.jpg.asset.json";
const hero = heroAsset.url;
const heroPicture = heroAsset.url;

export const Route = createFileRoute("/ranger-care")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Ranger Care Membership — Annual Shade & Awning Maintenance | Texas Made Shade",
      description: "Ranger Care is our annual membership for awnings, retractables, sail shades and covers — inspection, cleaning, priority service and warranty coverage across Texas.",
      image: hero,
      socialTitle: "Ranger Care Membership — Texas Made Shade",
      socialDescription: "Annual maintenance, priority repair, and warranty protection for your Texas Made Shade investment.",
      keywords: "ranger care membership, awning maintenance Texas, retractable awning service plan, shade maintenance membership, san antonio awning service, austin awning service, seguin awning maintenance, annual awning inspection, awning maintenance san antonio, awning maintenance austin, shade cleaning texas, retractable awning tune up new braunfels, awning service plan texas statewide",
    }),
    scripts: serviceHeadScripts({
      path: "/ranger-care",
      serviceName: "Ranger Care Membership",
      breadcrumbName: "Ranger Care",
      description: "Annual membership for inspection, cleaning, priority repair and warranty coverage on awnings, retractables, sail shades and custom covers.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/ranger-care"
      serviceName="Ranger Care"
      eyebrow="RANGER CARE — MEMBERSHIP"
      title="Ranger Care Membership — Annual Shade & Awning Care"
      intro="Ranger Care is our annual service membership for everything we build — retractable awnings, sail shades, patio curtains, pergola canopies, boat and RV covers, and welded frames. One yearly visit keeps motors, fabric, and hardware in working order, and members get priority scheduling when weather or wear does happen."
      heroImage={heroPicture}
      preContent={
        <div className="mb-6 flex justify-center">
          <SeriesBadge slug="ranger-care" size="md" priority />
        </div>
      }
      heroCta={{
        eyebrow: "Ranger Care · Statewide Texas",
        headline: "Join Ranger Care — Protect Your Shade Investment.",
        subhead: "Tell us what you own and where it lives. We'll send back a Ranger Care quote tailored to your setup, usually same business day.",
        primaryHref: "/estimate-quotes#general",
        primaryLabel: "Request Ranger Care Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      bodyLayout="accordion"
      features={[
        "Annual on-site inspection of frames, motors, fabric and hardware",
        "Fabric cleaning and re-treatment recommendations",
        "Motor, remote, wind-sensor and switch check for retractables",
        "Priority scheduling on repairs and storm damage",
        "Member discount on parts, labor and fabric replacement",
        "Warranty coverage extension on qualifying builds",
      ]}
      applications={[
        "Residential patios and pool decks",
        "Restaurants, storefronts and commercial patios",
        "Schools, churches and playgrounds",
        "Boats, RVs, wagons and stock trailers",
        "Ranch equipment, dog boxes and hunting rigs",
      ]}
      keywordOutline={[
        { id: "overview", label: "What Ranger Care Covers" },
        { id: "why-us", label: "Why a Ranger Care Membership" },
        { id: "details", label: "What Happens on a Visit" },
        { id: "applications", label: "What We Service" },
        { id: "faqs", label: "Ranger Care FAQs" },
      ]}
      body={[
        {
          heading: "What Ranger Care Covers",
          text: "One annual visit from our Seguin crew — the same people who built your shade. We inspect the frame, hardware, motors and fabric, clean and tighten what needs it, and flag anything that could turn into a bigger repair.",
          bullets: [
            "Frame, weld and bracket inspection",
            "Motor, wind sensor, remote and switch testing on retractables",
            "Fabric cleaning recommendations and spot treatment",
            "Hardware tightening, lubrication and adjustment",
            "Written condition report with photos",
          ],
        },
        {
          heading: "Why Members Get Priority",
          text: "When a Texas storm rolls through and half the county needs a repair, Ranger Care members go to the front of the line. We keep your build on file — fabric spec, motor model, install date — so we can quote parts and get out to you fast.",
          bullets: [
            "Front-of-line scheduling for repairs",
            "Discounted labor and parts",
            "Extended warranty coverage on qualifying builds",
            "Faster storm-damage response",
          ],
        },
        {
          heading: "What We Service Under Ranger Care",
          text: "Ranger Care applies to anything we've built or installed — and in most cases, to shade products from other builders too. Ask us what fits.",
          bullets: [
            "Retractable awnings (Solair® and custom)",
            "Sail shades and tension structures",
            "Patio curtains and drop shades",
            "Pergola, gazebo and patio covers",
            "Boat, RV, wagon and stock trailer covers",
            "Welded aluminum and steel frames",
          ],
        },
      ]}
    />
  ),
});
